import  { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast, ToastContainer } from 'react-toastify';
import generateRandomString from '../utils/generate-random-string';
import { ref, set, update } from 'firebase/database';
import { ref as storage_ref } from 'firebase/storage'
import { UserAuth } from '../context/AuthContext';
import { database, storage } from '../firebase_config/firebase';
import { FileUpload, InsertDriveFile } from '@mui/icons-material';
import uploadFile from '../utils/upload-file';
import ConfirmDialog from './confirm_dialog';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const projectTypes = [
    'Questionnaire',
    'Sensory Evaluation',
    'Lab Analysis',
    'Other (contact us)',
];

export default function NewProjectDrawer({ 
    open, 
    onClose, 
    currentProject, 
    onProjectCreated, 
    action 
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = UserAuth();
    const navigate = useNavigate();

    const [project, setProject] = useState({
        'id': '',
        'username': '',
        'email': '',
        'phone': '',
        'name': '',
        'type': '',
        'status': 'CREATED',
        'amount_paid': 0,
    });


    const [loading, setLoading] = useState(false);
    
    const [sampleSize, setSampleSize] = useState(0);

    const [selectedFile, setSelectedFile] = useState(null);

    const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
    const [extraMessage, setExtraMessage] = useState('');

    const [issue, setIssue] = useState('');

    const createButtonDisabled = !project.username || !project.email || !project.phone || !project.name || !project.type || loading;
    const startProjectDisabled = !currentProject || loading || (project.type === 'Sensory Evaluation' ? !sampleSize : (!selectedFile && !selectedQuestionnaire));
    const requestButtonDisabled = !currentProject || loading || (action === 'REQUEST_REVIEW' && !issue)

    const [startProjectDialog, setStartProjectDialog] = useState(false);

    const handleProjectChange = (key, value) => {
        setProject((prev) => ({
            ...prev,
            [key]: value
        }));
    };


    const createProject = async () => {
        try {
            setLoading(true);
            const updatedProject = {
                ...project,
                id: project.name.trim() + generateRandomString(6),
                timestamp: new Date().toISOString()
            };

            const projectRef = ref(database, 'Users/' + user.uid + '/Projects/' + updatedProject.id);
            await set(projectRef, updatedProject);
            onClose();
            toast.success('Project Created');
            onProjectCreated(updatedProject)
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const startProject = async () => {
        try {
            setStartProjectDialog(false);
            setLoading(true);

            const projectRef = ref(database, 'Users/' + user.uid + '/Projects/' + project.id);
            const adminRef = ref(database, 'AwaitingLinks/' + project.id);

            let updatedProject = {
                ...project,
            }

            if (project.type === 'Sensory Evaluation') {
                updatedProject = {
                    ...updatedProject,
                    sample_size: sampleSize,
                };
            } else {
                if (selectedQuestionnaire) {
                    updatedProject = {
                        ...updatedProject,
                        questionnaire_name: selectedQuestionnaire
                    }
                } else {
                    const fileRef = storage_ref(storage, `UploadedProjectFiles/${user.uid}/${project.id}/${selectedFile.name}`);

                    const url = await uploadFile(fileRef, selectedFile);

                    if (!url) {
                        toast.error('an error occured');
                        return;
                    }

                    updatedProject = {
                        ...updatedProject,     
                        file_url: url,
                    };
                }
            }
            
            updatedProject = {
                ...updatedProject,
                status: 'AWAITING_URL',
                extra_message: extraMessage
            };

            
            await Promise.all([
                set(projectRef, updatedProject),
                set(adminRef, {
                    user_id: user.uid,
                    project_id: project.id,
                    timestamp: new Date().toISOString()
                }),
                axios.post(`${API_URL}/email/project-created`, {
                    recipient: 'result-admin',
                    project_id: project.id
                }),
            ]);
            setSelectedFile(null);
            setExtraMessage('');
            onProjectCreated(updatedProject);
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const requestReview = async () => {
        try {
            setLoading(true);

            const adminRef = ref(database, 'AwaitingResults/' + project.id);
            const projectRef = ref(database, 'Users/' + user.uid + '/Projects/' + project.id);

            await Promise.all([
                set(adminRef, {
                    user_id: user.uid,
                    project_id: project.id,
                    issue: issue,
                    timestamp: new Date().toISOString()
                }),
                update(projectRef, {
                    status: 'PENDING_ISSUE'
                }),
                axios.post(`${API_URL}/email/result-disagree`, {
                    recipient: 'result-admin',
                    project_id: project.id
                }),
            ]);
            setIssue('');
            toast.success("we'll get back to you!");
            onProjectCreated({
                ...project,
                status: 'PENDING_ISSUE'
            });
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

        
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            if (file.size > 20 * 1024 * 1024) {
                alert("File size exceeds 20MB limit.");
                return;
            }
            if (!['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(file.type)) {
                alert("Only PDF and DOC/DOCX files are allowed.");
                return;
            }
            setSelectedFile(file);                                          
        }
    };

    const handleDragOver = (event) => {
        event.preventDefault();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        const file = event.dataTransfer.files[0];
        if (file) {
            if (file.size > 20 * 1024 * 1024) {
                alert("File size exceeds 20MB limit.");
                return;
            }
            if (!['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'].includes(file.type)) {
                alert("Only PDF and DOC/DOCX files are allowed.");
                return;
            }
            setSelectedFile(file);
        }
    }

    useEffect(() => {
        if (!currentProject) return;
        setProject(currentProject);
    }, [currentProject]);

    
    return (
        <Drawer
            anchor={isMobile ? 'bottom' : 'right'}
            open={open}
            onClose={onClose}
            variant="temporary"
            sx={{
                '& .MuiDrawer-paper': {
                    maxHeight: isMobile ? '90%' : '100%',
                    height: isMobile ? 'fit-content' : '100%',
                    width: isMobile ? '100%' : 400,
                    borderRadius: isMobile ? '20px 20px 0 0' : 0,
                },
            }}
        >
            <Box sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                    }}
                >

                    <Typography variant="h6" fontWeight="bold">
                        {action === 'UPDATE' ? (
                            "Update Project"
                        ) : action === 'REVIEW_REQUEST' ? (
                            'Request Result Review'
                        ) : (
                            'Create New Project'
                        )}
                    </Typography>

                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>
                
                <Divider sx={{ mb: 2 }} />

                <Box component="form" noValidate autoComplete="off">
                    {action === 'UPDATE' ? (
                         <>
                            {project.type === "Sensory Evaluation" && (
                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Sample Size"
                                    variant="outlined"
                                    multiline
                                    type='number'
                                    value={sampleSize}
                                    onChange={(event) => setSampleSize(event.target.value)}
                                />
                            )}

                            {project.type === "Questionnaire" && (
                                <Box sx={{ mt: 2 }}>

                                    <FormControl fullWidth sx={{ mb: 3 }}>
                                        <InputLabel id="questionnaire-select-label">
                                            Select Questionnaire or Upload Below
                                        </InputLabel>
                                        <Select
                                            labelId="questionnaire-select-label"
                                            value={selectedQuestionnaire}
                                            onChange={(e) => setSelectedQuestionnaire(e.target.value)}
                                            label="Select Existing Questionnaire or Upload Below"
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            <MenuItem value="q1">
                                                Prevalence of Overweight among Teachers
                                            </MenuItem>
                                            <MenuItem value="q2">
                                                Knowledge, Attitude and Practice of Exclusive Breast Feeding Among Lactating Mothers
                                            </MenuItem>
                                            <MenuItem value="q3">
                                                Food Consumption Pattern among Female Undergraduate Students
                                            </MenuItem>
                                            <MenuItem value="q4">
                                                Evaluating Food Intake On The Prevalence Of Ulcer Among Undergraduates
                                            </MenuItem>
                                            <MenuItem value="q5">
                                                Influence of maternal litracy on nutritional status of infant (0-3 years)
                                            </MenuItem>
                                            <MenuItem value="q6">
                                                Assessment of Breakfast Consumption Patterns Among Secondary School Students
                                            </MenuItem>
                                            <MenuItem value="q7">
                                                Assessment of Nutritional Knowledge and Practices on Prevailing Health Challenges Among Pregnant Women
                                            </MenuItem>
                                        </Select>
                                    </FormControl>

                                    {!selectedQuestionnaire && (
                                        <Box
                                            onDragOver={handleDragOver}
                                            onDrop={handleDrop}
                                            sx={{
                                                p: 4,
                                                border: '2px dashed',
                                                borderColor: 'grey.400',
                                                borderRadius: '12px',
                                                textAlign: 'center',
                                                backgroundColor: 'grey.50',
                                                cursor: 'pointer',
                                                transition: 'border-color 0.3s ease',
                                                '&:hover': {
                                                    borderColor: 'primary.main',
                                                },
                                            }}
                                        >
                                            <input
                                                accept=".pdf,.doc,.docx"
                                                style={{ display: 'none' }}
                                                id="file-upload"
                                                type="file"
                                                onChange={handleFileChange}
                                            />

                                            {selectedFile ? (
                                                <Box>
                                                    <InsertDriveFile color="primary" sx={{ fontSize: 40 }} />
                                                    <Typography
                                                        variant="body1"
                                                        sx={{
                                                            mt: 1,
                                                            maxWidth: '100%',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}
                                                    >
                                                        {selectedFile.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        File ready to upload.
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <label htmlFor="file-upload">
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <FileUpload color="primary" sx={{ fontSize: 60 }} />
                                                        <Typography variant="body1" sx={{ mt: 2, fontWeight: 'medium' }}>
                                                            Drag & Drop or <span style={{ color: '#1976d2' }}>Click to Upload</span>
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                                            PDF or DOC files, max 20MB
                                                        </Typography>
                                                    </Box>
                                                </label>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            <TextField
                                fullWidth
                                variant="outlined"
                                multiline
                                rows={4}
                                value={extraMessage}
                                onChange={(event) => setExtraMessage(event.target.value)}
                                placeholder='Any Extra Information (Optional)'
                                sx={{ mt: 2 }}
                            />
                        </>
                    ) : action === 'REVIEW_REQUEST' ? (
                        <>
                            <TextField
                                fullWidth
                                margin="normal"
                                variant="outlined"
                                multiline
                                rows={4}
                                type='number'
                                value={issue}
                                onChange={(event) => setIssue(event.target.value)}
                                placeholder='What is the issue?'
                            />
                        </>
                    ) : (
                        <>
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Full Name"
                                variant="outlined"
                                value={project.username}
                                onChange={(event) => handleProjectChange('username', event.target.value)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Email"
                                variant="outlined"
                                type="email"
                                value={project.email}
                                onChange={(event) => handleProjectChange('email', event.target.value)}
                            />
                            <TextField
                                fullWidth
                                margin="normal"
                                label="Phone Number"
                                variant="outlined"
                                value={project.phone}
                                onChange={(event) => handleProjectChange('phone', event.target.value)}
                            />
                           <TextField
                                fullWidth
                                margin="normal"
                                label="Project Name"
                                variant="outlined"
                                inputProps={{ maxLength: 15 }}
                                helperText="Max 15 characters"
                                value={project.name}
                                onChange={(event) => {
                                    const newValue = event.target.value;
                                    if (!newValue.includes(" ")) {
                                        handleProjectChange('name', newValue);
                                    }
                                }}
                            />
                            <TextField
                                fullWidth
                                select
                                margin="normal"
                                label="Project Type"
                                variant="outlined"
                                value={project.type}
                                onChange={(event) => handleProjectChange('type', event.target.value)}
                            >
                                {projectTypes.map((option) => (
                                    <MenuItem
                                        key={option}
                                        value={option}
                                        disabled={option === 'Other (contact us)'}
                                    >
                                        {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}

                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                        {action === 'UPDATE' ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => setStartProjectDialog(true)}
                                disabled={startProjectDisabled}
                                sx={{
                                    minWidth: 150, 
                                    height: 40,
                                }}
                            >
                                {loading ? <CircularProgress color='inherit' size={20} /> : "Start Project"}
                            </Button>
                        ) : action === 'REVIEW_REQUEST' ? (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={requestReview}
                                disabled={requestButtonDisabled}
                                sx={{
                                    minWidth: 150, 
                                    height: 40,
                                }}
                            >
                                {loading ? <CircularProgress color='inherit' size={20} /> : "Request"}
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={createProject}
                                disabled={createButtonDisabled}
                                sx={{
                                    minWidth: 150, 
                                    height: 40,
                                }}
                            >
                                {loading ? <CircularProgress color='inherit' size={20} /> : "Create Project"}
                            </Button>
                        )}
                       
                    </Box>
                </Box>
            </Box>

            <ConfirmDialog
                open={startProjectDialog}
                onClose={() => setStartProjectDialog(false)}
                onProceed={startProject}
                description={`This project will now start the creation process, you'll be notified via 
                    ${project.phone} or ${project.email} when this is completed or an error occurs.`
                }
            />

            <ToastContainer />
        </Drawer>
    );
}